import { useCallback, useEffect, useState } from "react";
import { PlanStep, SelectedPlanBar } from "../components/PlanStep";
import { SignStep } from "../components/SignStep";
import { ReviewStep } from "../components/ReviewStep";
import { PayStep } from "../components/PayStep";
import { DoneStep } from "../components/DoneStep";
import {
  createPaymentIntent,
  fetchOnboardingSession,
  type BillingPlan,
  type ExhibitAPayload,
  type OnboardingSession,
} from "../lib/api";

type Step =
  | "loading"
  | "plan"
  | "sign"
  | "review"
  | "pay"
  | "complete"
  | "error";

interface PersistedState {
  uid: string;
  step: Step;
  /** Absent on progress saved before plan choice existed (signed the weekly lease). */
  billingPlan?: BillingPlan;
  signedPdfBase64?: string;
  signerFullName?: string;
  exhibitA?: ExhibitAPayload;
  paymentIntentId?: string;
}

function lsKey(uid: string): string {
  return `ams-onboarding:${uid}`;
}

function loadPersisted(uid: string): PersistedState | null {
  try {
    const raw = localStorage.getItem(lsKey(uid));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedState;
    if (parsed.uid !== uid) return null;
    return parsed;
  } catch {
    return null;
  }
}

function savePersisted(state: PersistedState) {
  try {
    localStorage.setItem(lsKey(state.uid), JSON.stringify(state));
  } catch {
    /* storage may be unavailable; ignore */
  }
}

function clearPersisted(uid: string) {
  try {
    localStorage.removeItem(lsKey(uid));
  } catch {
    /* ignore */
  }
}

interface Props {
  uid: string;
  token: string;
}

export function OnboardingPage({ uid, token }: Props) {
  const [step, setStep] = useState<Step>("loading");
  const [session, setSession] = useState<OnboardingSession | null>(null);
  const [billingPlan, setBillingPlan] = useState<BillingPlan | null>(null);
  const [signedPdfBase64, setSignedPdfBase64] = useState<string>("");
  const [signerFullName, setSignerFullName] = useState<string>("");
  const [exhibitA, setExhibitA] = useState<ExhibitAPayload | null>(null);
  const [paymentIntentId, setPaymentIntentId] = useState<string>("");
  const [fatalError, setFatalError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await fetchOnboardingSession({ uid, token });
        if (cancelled) return;
        setSession(s);

        const persisted = loadPersisted(uid);
        if (s.onboardingStatus === "complete") {
          setBillingPlan(persisted?.billingPlan ?? null);
          setStep("complete");
          clearPersisted(uid);
          return;
        }

        if (
          persisted?.signedPdfBase64 &&
          persisted.signerFullName &&
          persisted.exhibitA
        ) {
          setBillingPlan(persisted.billingPlan ?? "weekly");
          setSignedPdfBase64(persisted.signedPdfBase64);
          setSignerFullName(persisted.signerFullName);
          setExhibitA(persisted.exhibitA);
          if (persisted.paymentIntentId) {
            setPaymentIntentId(persisted.paymentIntentId);
            setStep("complete");
          } else if (persisted.step === "review") {
            // Resume mid-review — they signed but never confirmed the preview.
            setStep("review");
          } else {
            setStep("pay");
          }
          return;
        }

        setStep("plan");
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setFatalError(String((err as Error)?.message ?? err));
          setStep("error");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [uid, token]);

  const handlePlanSelected = useCallback((plan: BillingPlan) => {
    setBillingPlan(plan);
    setStep("sign");
  }, []);

  const handleSigned = useCallback(
    (out: {
      signedPdfBase64: string;
      signerFullName: string;
      exhibitA: ExhibitAPayload;
    }) => {
      setSignedPdfBase64(out.signedPdfBase64);
      setSignerFullName(out.signerFullName);
      setExhibitA(out.exhibitA);
      savePersisted({
        uid,
        step: "review",
        billingPlan: billingPlan ?? undefined,
        signedPdfBase64: out.signedPdfBase64,
        signerFullName: out.signerFullName,
        exhibitA: out.exhibitA,
      });
      setStep("review");
    },
    [uid, billingPlan],
  );

  const handleReviewed = useCallback(() => {
    if (!signedPdfBase64 || !signerFullName || !exhibitA || !billingPlan) {
      // Shouldn't happen — the review step is only reachable via handleSigned
      // which populates all three. Fall back to re-signing rather than
      // continuing with a half-baked payload.
      setStep(billingPlan ? "sign" : "plan");
      return;
    }
    savePersisted({
      uid,
      step: "pay",
      billingPlan,
      signedPdfBase64,
      signerFullName,
      exhibitA,
    });
    setStep("pay");
  }, [uid, billingPlan, signedPdfBase64, signerFullName, exhibitA]);

  const createIntent = useCallback(
    (opts: { couponCode?: string }) =>
      createPaymentIntent({
        uid,
        token,
        couponCode: opts.couponCode,
        billingPlan: billingPlan ?? "weekly",
      }),
    [uid, token, billingPlan],
  );

  const handleBackToSign = useCallback(() => {
    // Preserve the signed PDF / form snapshot in memory so the user isn't
    // forced to re-fill everything — but drop the persisted "review" step so
    // a refresh at this point sends them back to sign.
    clearPersisted(uid);
    setSignedPdfBase64("");
    setExhibitA(null);
    setStep("sign");
  }, [uid]);

  const handlePaid = useCallback(
    (piId: string) => {
      setPaymentIntentId(piId);
      savePersisted({
        uid,
        step: "complete",
        billingPlan: billingPlan ?? undefined,
        signedPdfBase64,
        signerFullName,
        exhibitA: exhibitA ?? undefined,
        paymentIntentId: piId,
      });
      setStep("complete");
    },
    [uid, billingPlan, signedPdfBase64, signerFullName, exhibitA],
  );

  if (step === "loading") {
    return (
      <div className="py-16 text-center text-sm text-slate-500">
        Loading your enrollment…
      </div>
    );
  }

  if (step === "error") {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-5">
        <h2 className="text-lg font-semibold text-red-900">
          We can't open your enrollment link
        </h2>
        <p className="mt-1 text-sm text-red-800">{fatalError}</p>
        <p className="mt-3 text-sm text-red-800">
          The link may have expired (links are valid for 14 days). Please
          contact AMS to be re-invited.
        </p>
      </div>
    );
  }

  if (!session) return null;

  return (
    <div className="space-y-8">
      <Stepper current={step} />

      {step === "plan" ? (
        <PlanStep
          pricing={session.pricing}
          initialPlan={billingPlan}
          onSelected={handlePlanSelected}
        />
      ) : null}

      {step === "sign" && billingPlan ? (
        <>
          <SelectedPlanBar
            plan={billingPlan}
            pricing={session.pricing}
            onChange={setBillingPlan}
          />
          <SignStep
            leaseTemplateUrl={session.leaseTemplateUrls[billingPlan]}
            initialFullName={session.displayName ?? ""}
            prefill={session.prefill}
            onSigned={handleSigned}
          />
        </>
      ) : null}

      {step === "review" && signedPdfBase64 ? (
        <ReviewStep
          signedPdfBase64={signedPdfBase64}
          onContinue={handleReviewed}
          onBack={handleBackToSign}
        />
      ) : null}

      {step === "pay" && billingPlan ? (
        <PayStep
          pricing={session.pricing}
          billingPlan={billingPlan}
          createIntent={createIntent}
          onPaid={handlePaid}
        />
      ) : null}

      {step === "complete" ? (
        <DoneStep
          uid={uid}
          token={token}
          signedPdfBase64={signedPdfBase64}
          signerFullName={signerFullName}
          exhibitA={exhibitA}
          paymentIntentId={paymentIntentId}
          loginEmail={session.email}
          password={session.password}
          alreadyComplete={
            session.onboardingStatus === "complete" || !paymentIntentId
          }
          pricing={session.pricing}
          billingPlan={billingPlan}
        />
      ) : null}
    </div>
  );
}

function Stepper({ current }: { current: Step }) {
  const labels: { id: Step; label: string }[] = [
    { id: "plan", label: "Plan" },
    { id: "sign", label: "Sign" },
    { id: "review", label: "Review" },
    { id: "pay", label: "Payment" },
    { id: "complete", label: "Done" },
  ];
  const currentIdx = labels.findIndex((l) => l.id === current);
  return (
    <ol className="flex items-center justify-center gap-2 text-xs sm:text-sm">
      {labels.map((l, i) => {
        const isActive = i === currentIdx;
        const isDone = i < currentIdx;
        return (
          <li key={l.id} className="flex items-center gap-2">
            <span
              className={
                "flex h-7 w-7 items-center justify-center rounded-full border text-xs font-semibold " +
                (isDone
                  ? "bg-brand-500 border-brand-500 text-white"
                  : isActive
                    ? "border-brand-500 text-brand-600 bg-white"
                    : "border-slate-300 text-slate-400 bg-white")
              }
            >
              {isDone ? "✓" : i + 1}
            </span>
            <span
              className={
                isActive
                  ? "text-slate-900 font-medium"
                  : isDone
                    ? "text-slate-600"
                    : "text-slate-400"
              }
            >
              {l.label}
            </span>
            {i < labels.length - 1 ? (
              <span className="mx-2 h-px w-6 bg-slate-300" aria-hidden />
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}
